import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/categories")({
  component: CategoriesPage,
  head: () => ({ meta: [{ title: "Categories — Admin" }, { name: "robots", content: "noindex" }] }),
});

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
}

function CategoriesPage() {
  return (
    <AdminLayout title="Categories">
      <TaxonomyManager table="categories" labelSingular="Category" labelPlural="Categories" />
    </AdminLayout>
  );
}

export function TaxonomyManager({
  table,
  labelSingular,
  labelPlural,
}: {
  table: "categories" | "tags";
  labelSingular: string;
  labelPlural: string;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState("");

  const { data: items } = useQuery({
    queryKey: [table],
    queryFn: async () => (await supabase.from(table).select("id, name, slug").order("name")).data ?? [],
  });

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const { error } = await supabase.from(table).insert({ name: name.trim(), slug: slugify(name) });
    if (error) return toast.error(error.message);
    setName("");
    qc.invalidateQueries({ queryKey: [table] });
  };

  const remove = async (id: string) => {
    if (!confirm("Delete?")) return;
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) return toast.error(error.message);
    qc.invalidateQueries({ queryKey: [table] });
  };

  return (
    <div className="grid lg:grid-cols-[320px_1fr] gap-6">
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <h2 className="font-semibold text-slate-800 mb-4">Add new {labelSingular}</h2>
        <form onSubmit={add} className="space-y-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`${labelSingular} name`} />
          <Button type="submit" className="bg-sky-600 hover:bg-sky-700">
            <Plus className="h-4 w-4 mr-1" /> Add {labelSingular}
          </Button>
        </form>
      </div>
      <div className="bg-white border border-slate-200 rounded-md">
        <div className="px-5 py-3 border-b border-slate-200 font-semibold text-slate-800">{labelPlural}</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
            <tr><th className="text-left px-5 py-2">Name</th><th className="text-left px-5 py-2">Slug</th><th /></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(items ?? []).length === 0 ? (
              <tr><td colSpan={3} className="text-center py-8 text-slate-500">No {labelPlural.toLowerCase()} yet</td></tr>
            ) : items?.map((it) => (
              <tr key={it.id}>
                <td className="px-5 py-2.5 font-medium text-slate-800">{it.name}</td>
                <td className="px-5 py-2.5 text-slate-500 font-mono text-xs">{it.slug}</td>
                <td className="px-5 py-2.5 text-right">
                  <button onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-600 p-1.5">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
